import {
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import { Space, Tag } from "antd";
import type { ColumnsType } from "antd/es/table";
import { TableActionButtons } from "../../Components/TableActionButtons";
import {
  formatDataLockDate,
  formatDataLockDateTime,
  getActionLabel,
  getActorLabel,
  getFeatureLabel,
  getScopeSummary,
  getStatusDisplay,
} from "./helpers";
import type { DataLockRecord } from "./types";

type BuildDataLockColumnsParams = {
  current: number;
  pageSize: number;
  onView: (id: string) => void;
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
  deletingId: string | null;
  canEdit: boolean;
  canDelete: boolean;
};

export const buildDataLockColumns = (
  {
    current,
    pageSize,
    onView,
    onEdit,
    onDelete,
    deletingId,
    canEdit,
    canDelete,
  }: BuildDataLockColumnsParams,
): ColumnsType<DataLockRecord> => [
  {
    title: "STT",
    key: "index",
    width: 72,
    fixed: "left",
    render: (_value: unknown, _record: DataLockRecord, index: number) =>
      (current - 1) * pageSize + index + 1,
  },
  {
    title: "Tên khóa",
    dataIndex: "name",
    width: 220,
    fixed: "left",
    render: (value: string) => value || "-",
  },
  {
    title: "Ngày hiệu lực",
    dataIndex: "from",
    width: 140,
    render: (value: string | null | undefined) => formatDataLockDate(value),
  },
  {
    title: "Ngày kết thúc",
    dataIndex: "to",
    width: 140,
    render: (value: string | null | undefined) => formatDataLockDate(value),
  },
  {
    title: "Loại khóa",
    dataIndex: "feature",
    width: 190,
    render: (value: DataLockRecord["feature"]) => getFeatureLabel(value),
  },
  {
    title: "Hành động",
    dataIndex: "actions",
    width: 220,
    render: (actions: unknown) => {
      const normalizedActions = Array.isArray(actions)
        ? actions.filter(
            (action): action is string =>
              typeof action === "string" && action.length > 0,
          )
        : [];

      if (!normalizedActions.length) {
        return "-";
      }

      return (
        <Space size={[0, 4]} wrap>
          {normalizedActions.map((action, index) => (
            <Tag color="blue" key={`action-${action}-${index}`}>
              {getActionLabel(action)}
            </Tag>
          ))}
        </Space>
      );
    },
  },
  {
    title: "Thuộc tính khóa",
    key: "restrictedScope",
    width: 240,
    render: (_value: unknown, record: DataLockRecord) => {
      const summaries = getScopeSummary(record, "restricted");
      if (!summaries.length) {
        return "-";
      }

      return (
        <Space size={[0, 4]} wrap>
          {summaries.map(({ label, count }, index) => (
            <Tag key={`restricted-${label}-${index}`}>
              {label} (+{count})
            </Tag>
          ))}
        </Space>
      );
    },
  },
  {
    title: "Thuộc tính loại trừ",
    key: "whitelistedScope",
    width: 240,
    render: (_value: unknown, record: DataLockRecord) => {
      const summaries = getScopeSummary(record, "whitelisted");
      if (!summaries.length) {
        return "-";
      }

      return (
        <Space size={[0, 4]} wrap>
          {summaries.map(({ label, count }, index) => (
            <Tag key={`whitelisted-${label}-${index}`}>
              {label} (+{count})
            </Tag>
          ))}
        </Space>
      );
    },
  },
  {
    title: "Trạng thái",
    dataIndex: "active",
    width: 120,
    render: (_active: boolean, record: DataLockRecord) => {
      const status = getStatusDisplay(record);
      return <Tag color={status.color}>{status.label}</Tag>;
    },
  },
  {
    title: "Người tạo",
    dataIndex: "createdBy",
    width: 180,
    render: (actor: DataLockRecord["createdBy"]) => getActorLabel(actor),
  },
  {
    title: "Ngày tạo",
    dataIndex: "createdAt",
    width: 180,
    render: (value: string | null | undefined) =>
      formatDataLockDateTime(value),
  },
  {
    title: "Thao tác",
    key: "tableActions",
    width: 136,
    fixed: "right",
    render: (_value: unknown, record: DataLockRecord) => (
      <TableActionButtons
        items={[
          {
            key: `view-${record._id}`,
            label: "Xem chi tiết",
            icon: <EyeOutlined />,
            tone: "info",
            onClick: () => onView(record._id),
          },
          {
            key: `edit-${record._id}`,
            label: "Chỉnh sửa",
            icon: <EditOutlined />,
            tone: "edit",
            disabled: !canEdit,
            onClick: () => onEdit(record._id),
          },
          {
            key: `delete-${record._id}`,
            label: "Xóa",
            icon: <DeleteOutlined />,
            tone: "delete",
            disabled: !canDelete || deletingId !== null,
            onClick: () => onDelete(record._id),
          },
        ]}
      />
    ),
  },
];
